import { createAction } from 'redux-act'

import { Thunk } from '../types'
import {
    INITIALIZE_FILTERS,
    SET_FILTERS_CONFIGURATION,
    COMMIT_FILTERS_CONFIGURATION,
    REVERT_FILTERS_CONFIGURATION,
    SET_FILTERS_ERROR,
    SET_LOADING,
    SET_BLOCKING,
    SET_CURRENT_OPENED_FILTER_MODAL,
    SET_LAST_DATA_PATH_ENCODED,
    OPEN_CANCELLATION_POLICY_MODAL,
    CLOSE_CANCELLATION_POLICY_MODAL,
} from './action-types'
import { InitializeFiltersPayload, FilterClickPayload } from './types'
import { FilterModalType, FilterType } from '../../components/app-components/filter-sort-section/types'
import { getRelativeURL, decodeFromBase64, encodeToBase64 } from '../../utils/encoding-utils'
import { searchClient } from '../../clients'
import FiltersConfigurationTransport from '../../transports/filters-configuration-transport'
import { RequestUserConfig } from '../../common/types'

// ============================================================================
// Sync Actions
// ============================================================================

export const initializeFilters = createAction<InitializeFiltersPayload>(INITIALIZE_FILTERS)
export const setFiltersConfiguration = createAction<FilterClickPayload>(SET_FILTERS_CONFIGURATION)
export const commitFiltersConfiguration = createAction(COMMIT_FILTERS_CONFIGURATION)
export const revertFiltersConfiguration = createAction(REVERT_FILTERS_CONFIGURATION)
export const setFiltersError = createAction<Error | null>(SET_FILTERS_ERROR)
export const setLoading = createAction<boolean>(SET_LOADING)
export const setBlocking = createAction<boolean>(SET_BLOCKING)
export const setCurrentOpenedFilterModal = createAction<FilterModalType | FilterType>(SET_CURRENT_OPENED_FILTER_MODAL)
export const setLastDataPathEncoded = createAction<string>(SET_LAST_DATA_PATH_ENCODED)
export const openCancellationPolicyModal = createAction(OPEN_CANCELLATION_POLICY_MODAL)
export const closeCancellationPolicyModal = createAction(CLOSE_CANCELLATION_POLICY_MODAL)

// ============================================================================
// API Helper
// ============================================================================

const _fetchFiltersConfiguration = async (
    user: RequestUserConfig,
    filter: string,
    clear?: string,
): Promise<FiltersConfigurationTransport> => {
    const response = await searchClient.getFiltersConfiguration({ user, filter, clear })
    return response.data!
}

// ============================================================================
// Thunks
// ============================================================================

/**
 * Handles a filter click: calls the filter API and updates state.
 */
export const filterClick =
    (encodedLink: string, shouldCommit: boolean, user: RequestUserConfig): Thunk<Promise<void>> =>
    async (dispatch, getState) => {
        dispatch(setLoading(true))
        try {
            const config = await _fetchFiltersConfiguration(user, getRelativeURL(encodedLink))
            dispatch(setFiltersConfiguration({ config, shouldCommit }))

            // Only update the URL state when the mobile filter modal is NOT open.
            // When the modal is open, URL updates are deferred until the submit button is clicked.
            const { filters } = getState()
            if (filters.currentOpenedFilterModal !== FilterModalType.FILTERS) {
                const relativePath = getRelativeURL(config.encodedLink)
                dispatch(setLastDataPathEncoded(relativePath))

                // When spaMode is false (e.g. map page), do a full page reload
                if (!filters.spaMode) {
                    window.location.href = decodeFromBase64(relativePath)
                }
            }
        } catch (error) {
            console.error({ message: 'Error occurred in filters configuration API', error })
            dispatch(setFiltersError(error as Error))
        } finally {
            dispatch(setLoading(false))
        }
    }

/**
 * Handles filter clear: calls the filter API with a clear parameter and commits.
 */
export const filterClear =
    (user: RequestUserConfig): Thunk<Promise<void>> =>
    async (dispatch, getState) => {
        const { filters } = getState()
        const filtersConfig = filters.filtersConfigurationResponse

        if (!filtersConfig) {
            return
        }

        dispatch(setLoading(true))
        try {
            const config = await _fetchFiltersConfiguration(user, getRelativeURL(filtersConfig.clearLink))

            dispatch(setFiltersConfiguration({ config, shouldCommit: true }))
            dispatch(setLastDataPathEncoded(getRelativeURL(config.encodedLink)))
        } catch (error) {
            console.error({ message: 'Error occurred in filters configuration API', error })
            dispatch(setFiltersError(error as Error))
        } finally {
            dispatch(setLoading(false))
        }
    }

/**
 * Handles filter submit: either navigates (map) or pushes state (list).
 */
export const filterSubmit =
    (encodedLink?: string, skipNavigation?: boolean): Thunk =>
    (dispatch, getState) => {
        const { filters } = getState()
        // Use the latest draft config path if available (covers mobile modal case
        // where filterClick deferred the lastDataPathEncoded update)
        const latestPath = filters.filtersConfigurationResponse
            ? getRelativeURL(filters.filtersConfigurationResponse.encodedLink)
            : filters.lastDataPathEncoded

        try {
            // Sync lastDataPathEncoded with the latest draft state
            dispatch(setLastDataPathEncoded(latestPath))

            if (!skipNavigation) {
                // Full page navigation (map view)
                window.location.href = encodedLink ? decodeFromBase64(encodedLink) : decodeFromBase64(latestPath)
            }
            // Commit draft → committed
            dispatch(commitFiltersConfiguration())
        } catch (error) {
            console.error({ message: 'Error occurred in filter submit', error })
        }
    }

/**
 * Fetches the initial filters configuration and initializes the Redux state.
 * Replaces the old useFiltersConfiguration hook + FiltersInitializer flow.
 */
export const fetchInitialFilters =
    (user: RequestUserConfig, regionNodeId: number, spaMode: boolean): Thunk<Promise<void>> =>
    async dispatch => {
        try {
            const filter = encodeToBase64(window.location.pathname + window.location.search)
            const config = await _fetchFiltersConfiguration(user, filter)
            dispatch(initializeFilters({ config, regionNodeId, spaMode }))
        } catch (error) {
            console.error({ message: 'Error occurred in initial filters fetch', error })
            dispatch(setFiltersError(error as Error))
        }
    }
